import { Div, Divider, Flex, Link, Stack, Tooltip, TooltipTrigger } from "@hopper-ui/components";
import { clamp } from "@sg-protect/components";
import {
    FeatureFlagKeys,
    type NavigationLinkRenderProps,
    type RenderItemFunction,
    isNavigationLink
} from "@sg-protect/federation-runtime";
import { useFeatureFlag, useNavigationItems, useRenderedNavigationItems } from "@sg-protect/federation-runtime/squide";
import config from "@sg-protect/runtime-config";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useCallback, useMemo, useRef, useState } from "react";
import { useShellTranslation } from "../../../i18n/i18next.ts";
import { FeedbackButton } from "../../feedback-button/FeedbackButton.tsx";
import { CollapseButton } from "./CollapseButton.tsx";
import { ProtectSidebarBottomId, ProtectSidebarId } from "./constants.ts";
import { Section } from "./Section.tsx";
import styles from "./Sidebar.module.css";
import { SidebarLink } from "./SidebarLink.tsx";

const LOCAL_STORAGE_KEY = "sg-protect-sidebar-width";

const COLLAPSED_WIDTH = 80;
const EXPANDED_MIN_WIDTH = 240;
const EXPANDED_MAX_WIDTH = 340;
const COLLAPSE_SCREEN_WIDTH = 1024;

export function Sidebar() {
    const { t } = useShellTranslation();
    const showArchivesLink = useFeatureFlag(FeatureFlagKeys.ShowArchivesLink, false);
    const [isHover, setIsHover] = useState(false);
    const [isCollapseButtonFocused, setIsCollapseButtonFocused] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [width, setWidth] = useLocalStorage(
        LOCAL_STORAGE_KEY,
        window.innerWidth <= COLLAPSE_SCREEN_WIDTH ? COLLAPSED_WIDTH : EXPANDED_MIN_WIDTH
    );
    const sidebarRef = useRef<HTMLDivElement>(null);
    const isExpanded = useMemo(() => width > COLLAPSED_WIDTH, [width]);

    const navigationItems = useNavigationItems({ menuId: ProtectSidebarId });
    const bottomNavigationItems = useNavigationItems({ menuId: ProtectSidebarBottomId });

    const renderItem = useCallback<RenderItemFunction>(
        (item, key) => {
            if (isNavigationLink(item)) {
                return (
                    <SidebarLink
                        key={key}
                        icon={item.additionalProps.icon}
                        badge={item.additionalProps.badge}
                        size={isExpanded ? "expanded" : "collapsed"}
                        {...item.linkProps}
                    >
                        {item.label}
                    </SidebarLink>
                );
            } else {
                return (
                    <Stack key={key} gap="stack-sm" marginBottom={isExpanded ? "stack-lg" : "stack-sm"}>
                        {isExpanded && item.label && <Section paddingX="inset-sm">{item.label}</Section>}
                        {item.section}
                    </Stack>
                );
            }
        },
        [isExpanded]
    );

    const navigationElements = useRenderedNavigationItems(navigationItems, renderItem, el => el);
    const bottomNavigationElements = useRenderedNavigationItems(bottomNavigationItems, renderItem, el => el);

    const handleCollapse = () => {
        const newWidth = isExpanded ? COLLAPSED_WIDTH : EXPANDED_MIN_WIDTH;
        setWidth(newWidth);

        if (sidebarRef.current) {
            const sidebar = sidebarRef.current;

            sidebar.animate(
                [
                    { width: `${sidebar.clientWidth}px`, minWidth: "auto" },
                    { width: `${newWidth}px`, minWidth: "auto" }
                ],
                { duration: 100 }
            );
        }
    };

    const handleResize = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);

        const offsetLeft = sidebarRef.current ? sidebarRef.current.getBoundingClientRect().left : 0;

        const breakpoint = (COLLAPSED_WIDTH + EXPANDED_MIN_WIDTH) / 2;

        // To avoid stale closures from refering the states directly, we declare
        // a mutable copy here which is used between calls to the
        // `handleMouseMove` function
        let isExpandedRef = isExpanded;

        document.body.style.cssText = "cursor: ew-resize !important";

        const controller = new AbortController();

        document.addEventListener(
            "mousemove",
            (mouseMoveEvent: MouseEvent) => {
                mouseMoveEvent.preventDefault();
                mouseMoveEvent.stopPropagation();
                const desiredWidth = mouseMoveEvent.clientX - offsetLeft;

                if (isExpandedRef && desiredWidth < breakpoint) {
                    isExpandedRef = false;
                }

                if (!isExpandedRef && desiredWidth > breakpoint) {
                    isExpandedRef = true;
                }

                if (isExpandedRef) {
                    const clampedWidth = clamp(desiredWidth, EXPANDED_MIN_WIDTH, EXPANDED_MAX_WIDTH);
                    setWidth(clampedWidth);
                } else {
                    setWidth(COLLAPSED_WIDTH);
                }
            },
            { signal: controller.signal }
        );

        document.addEventListener(
            "mouseup",
            mouseUpEvent => {
                mouseUpEvent.preventDefault();
                mouseUpEvent.stopPropagation();
                setIsResizing(false);
                document.body.style.cursor = "";
                controller.abort();
            },
            { once: true }
        );
    };

    return (
        <Div
            ref={sidebarRef}
            UNSAFE_width={`${width}px`}
            backgroundColor="neutral"
            borderRight="neutral-weak"
            position="relative"
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            <Div
                className={styles["sidebarSash"]}
                onMouseDown={handleResize}
                position="absolute"
                top={0}
                bottom={0}
                left="100%"
                width="core_20"
                transform="translateX(-50%)"
                cursor="ew-resize"
                backgroundColor="neutral-strong"
                opacity={isResizing ? 1 : 0}
                style={{ transition: "opacity var(--hop-easing-duration-1) var(--hop-easing-focus)" }}
            />
            <CollapseButton
                className={styles["sidebarCollapseButton"]}
                zIndex={1}
                onClick={handleCollapse}
                onFocus={() => setIsCollapseButtonFocused(true)}
                onBlur={() => setIsCollapseButtonFocused(false)}
                title={isExpanded ? t("Sidebar.collapse") : t("Sidebar.expand")}
                aria-label={isExpanded ? t("Sidebar.collapse") : t("Sidebar.expand")}
                direction={isExpanded ? "left" : "right"}
                opacity={(isHover && !isResizing) || isCollapseButtonFocused ? 1 : 0}
                position="absolute"
                left="100%"
                top="16px"
                transform="translateX(calc(-50% + 1px))"
            />
            <Flex
                direction="column"
                justifyContent="space-between"
                height="100%"
                width="100%"
                overflowX="hidden"
                overflowY="auto"
            >
                <Stack
                    padding="inset-sm"
                    UNSAFE_width={`${width}px`}
                    borderBottom={isExpanded ? undefined : "neutral-weak"}
                    gap="stack-sm"
                >
                    {navigationElements}
                    {bottomNavigationItems.length > 0 && (
                        <>
                            <Divider marginY="core_80" />
                            {bottomNavigationElements}
                        </>
                    )}
                </Stack>
                {isExpanded && (
                    <Div>
                        {showArchivesLink && <Divider UNSAFE_marginX="-16px" />}

                        <Flex direction="column" margin="inline-sm" gap="stack-md">
                            {showArchivesLink && <ArchivesLink />}
                            <FeedbackButton />
                        </Flex>
                    </Div>
                )}
            </Flex>
        </Div>
    );
}

interface MobileSidebarProps {
    open: boolean;
    close: () => void;
}

export function MobileSidebar({ open, close }: MobileSidebarProps) {
    const navigationItems = useNavigationItems({ menuId: ProtectSidebarId });
    const bottomNavigationItems = useNavigationItems({ menuId: ProtectSidebarBottomId });

    const renderItem = useCallback<RenderItemFunction>(
        (item, key) => {
            if (isNavigationLink(item)) {
                return <MobileNavItem key={key} item={item} open={open} close={close} />;
            } else {
                return (
                    <Stack key={key} gap="stack-sm" marginBottom="stack-lg">
                        {item.label && <Section paddingX="inset-sm">{item.label}</Section>}
                        {item.section}
                    </Stack>
                );
            }
        },
        [close, open]
    );

    const navigationElements = useRenderedNavigationItems(navigationItems, renderItem, el => el);
    const bottomNavigationElements = useRenderedNavigationItems(bottomNavigationItems, renderItem, el => el);

    return (
        <Div
            zIndex={6000}
            position="absolute"
            top={0}
            bottom={0}
            backgroundColor="neutral-weakest"
            width="100%"
            padding="inset-sm"
            style={{ transition: "all var(--hop-easing-duration-2) var(--hop-easing-focus)" }}
            opacity={open ? 1 : 0}
            transform={open ? "translateX(0)" : "translateX(-100%)"}
            borderRight="neutral-weak"
            overflowY="auto"
            data-public
        >
            {navigationElements}
            {bottomNavigationItems.length > 0 && (
                <>
                    <Divider marginY="core_80" />
                    {bottomNavigationElements}
                </>
            )}
        </Div>
    );
}

interface MobileNavItemProps {
    item: NavigationLinkRenderProps;
    open: boolean;
    close: () => void;
}

function MobileNavItem({ item, open, close }: MobileNavItemProps) {
    return (
        <SidebarLink
            tabIndex={open ? 0 : -1}
            onClick={close}
            icon={item.additionalProps.icon}
            badge={item.additionalProps.badge}
            {...item.linkProps}
        >
            {item.label}
        </SidebarLink>
    );
}

function ArchivesLink() {
    const { t } = useShellTranslation();

    return (
        <TooltipTrigger>
            <Link
                href={config.get().archives.url}
                isExternal
                variant="secondary"
                isStandalone
                size="sm"
                paddingX="inset-sm"
                data-public
            >
                {t("Sidebar.archives.label")}
            </Link>
            <Tooltip data-public>{t("Sidebar.archives.tooltip")}</Tooltip>
        </TooltipTrigger>
    );
}
